import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUserProfile } from '../../Components/Auth/UserProfileContext';
import HamburgerButton from '../../Components/ui/HamburgerButton';
import FloatingMenuTab from '../../Components/ui/FloatingMenuTab';
import { X } from 'lucide-react';
import { useDashboardTour } from '../../hooks/useDashboardTour'; 

// Icon imports
import IconDash from '../../assets/Icons/IconDash.png';
import IconPublProfile from '../../assets/Icons/IconPublprofile.png';
import IconUpcomEvent from '../../assets/Icons/IconUpcomEvent.png';
import IconMessages from '../../assets/Icons/IconMessages.png';
import IconAnalytics from '../../assets/Icons/IconAnalytics.png';
import IconNews from '../../assets/Icons/IconNews.png';
import IconSettings from '../../assets/Icons/IconSettings.png';
import IconCalendar from '../../assets/Icons/IconCalendar.png';
import TourIcon from '../../assets/Icons/TourIcon.png';
import IconFlightHotel from '../../assets/Icons/IconFlightHotel.png';
import IconRiders from '../../assets/Icons/IconRiders.png';
import IconContract from '../../assets/Icons/IconContract.png';
import IconRating from '../../assets/Icons/IconRating.png';
import IconVerification from '../../assets/Icons/IconVerified2.png';
import IconNotifications from '../../assets/Icons/IconUrgent.png';
import GigApplicIcon from '../../assets/Icons/GigApplicIcon.png';
import RiderManageIcon from '../../assets/Icons/RiderManageIcon.png';
import AnimatedBellIcon from '../../Components/Notifications/AnimatedBellIcon';
import debug from '../../utils/debug';

// Simple hint management for mobile menu (no auto-hide)
const useMenuHint = () => {
  const [hasShownHint, setHasShownHint] = useState(() => {
    return localStorage.getItem('menuHintShown') === 'true';
  });
  
  // Show hint for first-time users
  useEffect(() => {
    if (!hasShownHint) {
      const timer = setTimeout(() => {
        setHasShownHint(true);
        localStorage.setItem('menuHintShown', 'true');
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [hasShownHint]);
  
  return { showHint: !hasShownHint };
}; 

const SidebarItem = ({ icon, text, onClick, isActive, isComingSoon, isDev, scaleFactor = 1, dataTour, isNotificationItem, userId }) => (
  <li
    onClick={onClick}
    data-tour={dataTour}
    style={{
      display: 'flex',
      alignItems: 'left',
      fontFamily: 'Montserrat, sans-serif',
      fontWeight: 'bold',
      fontSize: `${14 * scaleFactor}px`,
      marginBottom: `${30 * scaleFactor}px`,
      cursor: isComingSoon && process.env.NODE_ENV !== 'development' ? 'not-allowed' : 'pointer',
      color: isComingSoon ? '#888' : (isActive ? '#f85008' : 'white'),
      opacity: isComingSoon ? 0.6 : 1,
      transition: 'transform 0.2s ease'
    }}
    onMouseEnter={(e) => {
      if (!isComingSoon) {
        e.currentTarget.style.transform = `translateX(${10 * scaleFactor}px)`;
      }
    }}
    onMouseLeave={(e) => {
      if (!isComingSoon) {
        e.currentTarget.style.transform = 'translateX(0)';
      }
    }}
  >
    {isNotificationItem ? (
      <div style={{ width: `${22 * scaleFactor}px`, height: `${22 * scaleFactor}px`, marginRight: `${15 * scaleFactor}px` }}>
        <AnimatedBellIcon
          userId={userId}
          userType="talent"
          iconSrc={icon}
          className=""
        />
      </div>
    ) : (
      <img src={icon} alt={`${text} Icon`} style={{ width: `${22 * scaleFactor}px`, height: `${22 * scaleFactor}px`, marginRight: `${15 * scaleFactor}px`, filter: isComingSoon ? 'grayscale(100%)' : 'none' }} />
    )}
    {text}
    {isComingSoon && (
      <span style={{
        marginLeft: 'auto',
        fontSize: `${11 * scaleFactor}px`,
        fontWeight: 'normal',
        fontStyle: 'italic',
        color: '#aaa',
        backgroundColor: 'rgba(255,255,255,0.1)',
        padding: `${2 * scaleFactor}px ${8 * scaleFactor}px`,
        borderRadius: `${10 * scaleFactor}px`
      }}>
        Coming Soon
      </span>
    )}
    {isDev && (
      <span style={{
        marginLeft: 'auto',
        fontSize: `${11 * scaleFactor}px`,
        fontWeight: 'bold',
        color: '#fff',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: `${2 * scaleFactor}px ${8 * scaleFactor}px`,
        borderRadius: `${10 * scaleFactor}px`
      }}>
        DEV
      </span>
    )}
  </li>
);

const TalentSidebar = ({ currentUser, isMobileOpen = false, onMobileClose, scaleFactor: propScaleFactor }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [internalScaleFactor, setInternalScaleFactor] = useState(1);

  // Tour management
  const { resetTourState } = useDashboardTour();
  
  // Use prop scaleFactor if provided, otherwise calculate internally
  const scaleFactor = propScaleFactor !== undefined ? propScaleFactor : internalScaleFactor;
  
  // Calculate scale factor based on viewport width (1200px-1900px) only if not provided as prop
  useEffect(() => {
    if (propScaleFactor === undefined) {
      const calculateScale = () => {
        const width = window.innerWidth;
        if (width >= 1900) {
          setInternalScaleFactor(1);
        } else if (width <= 1200) {
          setInternalScaleFactor(0.75); // Minimum scale at 1200px
        } else {
          // Linear interpolation between 0.75 and 1 for widths 1200-1900
          const scale = 0.75 + ((width - 1200) / 700) * 0.25;
          setInternalScaleFactor(scale);
        }
      };
      
      calculateScale();
      window.addEventListener('resize', calculateScale);
      return () => window.removeEventListener('resize', calculateScale);
    }
  }, [propScaleFactor]);
  
  // Add hint management hook
  const { showHint } = useMenuHint();
  

  const handleNavigation = (page, path, isComingSoon) => {
    // Allow navigation in development mode or if not coming soon
    if (process.env.NODE_ENV === 'development' || !isComingSoon) {
      navigate(path);
    }
    // Don't close mobile menu after navigation per user preference
  };

  // Tour trigger handler
  const handleStartTour = () => {
    // Only allow tour functionality in development mode
    if (process.env.NODE_ENV !== 'development') {
      return; // Do nothing in production
    }

    console.log('TalentSidebar: handleStartTour called');
    console.log('TalentSidebar: resetTourState function:', resetTourState);
    resetTourState();
    // Close mobile menu if open
    if (isMobileOpen && onMobileClose) onMobileClose();
    setIsMobileMenuOpen(false);
  };

  const userId = currentUser?.uid;
  const { userProfiles } = useUserProfile();
  const userProfile = userProfiles?.[userId];
  const displayName = userProfile?.displayName || 'User';

  // Define the EPK path - use the correct route format from App.js
  const getEpkPath = useMemo(() => {
    return `/talent-profile/${userId}`;
  }, [userId]);

  // Define the mapping of sidebar items to their paths
  const sidebarItems = [
    { text: "Dashboard", path: `/talent-dashboard/${userId}`, icon: IconDash, dataTour: "sidebar-dashboard" },
    { text: "Public Profile", path: getEpkPath, icon: IconPublProfile, dataTour: "sidebar-public-profile" },
    { text: "Notifications", path: `/talent-notifications/${userId}`, icon: IconNotifications, dataTour: "sidebar-notifications" },
    { text: "Inbox", path: `/talent-inbox/${userId}`, icon: IconMessages, dataTour: "sidebar-inbox" },
    { text: "Gig Calendar", path: `/talent-calendar/${userId}`, icon: IconCalendar, dataTour: "sidebar-gig-calendar" },
    { text: "Gig Applications", path: `/talent-applications/${userId}`, icon: GigApplicIcon, dataTour: "sidebar-gig-applications" },
    { text: "Bookings Management", path: `/booking-management/${userId}`, icon: IconUpcomEvent },
    { text: "Rider Management", path: `/talent-riders/${userId}`, icon: RiderManageIcon },
    { text: "Contracts Locker", path: `/talent-contracts-locker/${userId}`, icon: IconContract },
    { text: "Analytics", path: `/talent-analytics/${userId}`, icon: IconAnalytics, dataTour: "sidebar-analytics" },
    { text: "Ratings & Reviews", path: `/talent-rating/${userId}`, icon: IconRating },
    { text: "Settings", path: `/talent-settings/${userId}`, icon: IconSettings, dataTour: "sidebar-settings" },
    { text: "Dashboard Tour", path: null, icon: TourIcon, isTour: true, isComingSoon: process.env.NODE_ENV === 'production' },
    { text: "News", path: `/talent-news/${userId}`, icon: IconNews, isComingSoon: true },
    { text: "Profile Verification", path: `/talent-verification/${userId}`, icon: IconVerification, isComingSoon: true },
  ];

  // Check if a path is the current active path
  const isActive = (path) => {
    if (path === getEpkPath) {
      // For EPK, check if the current path matches the generated EPK path
      return currentPath === path;
    }
    return currentPath === path;
  };

  // If mobile overlay mode, render as prominent overlay
  if (isMobileOpen) {
    return (
      <>
        {/* Mobile Overlay Background */}
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 10000,
            backdropFilter: 'blur(4px)'
          }}
          onClick={onMobileClose}
        />
        
        {/* Mobile Sidebar */}
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            height: '100vh',
            width: '300px',
            backgroundColor: 'linear-gradient(135deg, #144874 0%, #0d3659 100%)',
            background: 'linear-gradient(135deg, #144874 0%, #0d3659 100%)',
            color: 'white',
            zIndex: 10001,
            padding: '20px',
            overflowY: 'auto',
            boxShadow: '4px 0 20px rgba(0, 0, 0, 0.3)',
            transform: 'translateX(0)',
            transition: 'transform 0.3s ease-in-out'
          }}
        >
          {/* Close Button */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '30px',
            paddingBottom: '15px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <h2 style={{
              margin: 0,
              fontSize: '20px',
              fontWeight: 'bold',
              color: '#f85008'
            }}>Menu</h2>
            <button
              onClick={onMobileClose}
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                padding: '8px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              <X size={24} />
            </button>
          </div>
          
          {/* Menu Items */}
          <ul style={{ listStyleType: 'none', padding: 0, margin: 0 }}>
            {sidebarItems.map((item, index) => (
              <li
                key={index}
                onClick={() => {
                  handleNavigation(item.text, item.path, item.isComingSoon);
                  onMobileClose();
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  fontFamily: 'Montserrat, sans-serif',
                  fontWeight: 'bold',
                  fontSize: '16px',
                  marginBottom: '25px',
                  cursor: item.isComingSoon && process.env.NODE_ENV !== 'development' ? 'not-allowed' : 'pointer',
                  color: item.isComingSoon ? '#888' : (isActive(item.path) ? '#f85008' : 'white'),
                  opacity: item.isComingSoon ? 0.6 : 1,
                  padding: '12px 16px',
                  borderRadius: '8px',
                  transition: 'all 0.2s ease',
                  backgroundColor: isActive(item.path) ? 'rgba(248, 80, 8, 0.1)' : 'transparent'
                }}
                onMouseEnter={(e) => {
                  if (!item.isComingSoon) {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                    e.currentTarget.style.transform = 'translateX(5px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!item.isComingSoon) {
                    e.currentTarget.style.backgroundColor = isActive(item.path) ? 'rgba(248, 80, 8, 0.1)' : 'transparent';
                    e.currentTarget.style.transform = 'translateX(0)';
                  }
                }}
              >
                {item.text === "Notifications" ? (
                  <AnimatedBellIcon
                    userId={userId}
                    userType="talent"
                    iconSrc={item.icon}
                    className=""
                  />
                ) : (
                  <img
                    src={item.icon}
                    alt={`${item.text} Icon`} 
                    style={{
                      width: '24px',
                      height: '24px',
                      marginRight: '15px',
                      filter: item.isComingSoon ? 'grayscale(100%)' : 'none'
                    }}
                  />
                )}
                {item.text}
                {item.isComingSoon && (
                  <span style={{
                    marginLeft: '10px',
                    fontSize: '10px',
                    backgroundColor: '#f85008',
                    color: 'white',
                    padding: '2px 6px',
                    borderRadius: '10px'
                  }}>
                    SOON
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Desktop Sidebar */}
      <div className="sidebar desktop-sidebar" data-tour="talent-sidebar" style={{
        backgroundColor: 'transparent',
        color: 'white',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'left',
        padding: `${20 * scaleFactor}px`,
        paddingTop: `${20 * scaleFactor}px`, // Reduced top padding to move nav items closer to top
        width: `${284 * scaleFactor}px`, // Reduced by 16px (1rem)
        border: 'none',
        boxShadow: 'none',
        borderRight: 'none',
        borderImage: 'none',
        transition: 'all 0.3s ease'
      }}>
        <ul style={{ listStyleType: 'none', padding: 0, margin: 0, width: '100%' }}>
          {sidebarItems.map((item, index) => (
            <SidebarItem
              key={index}
              icon={item.icon}
              text={item.text}
              onClick={() => item.isTour ? handleStartTour() : handleNavigation(item.text, item.path, item.isComingSoon)}
              isActive={!item.isTour && isActive(item.path)}
              isComingSoon={item.isComingSoon}
              isDev={item.isDev}
              scaleFactor={scaleFactor}
              dataTour={item.dataTour}
              isNotificationItem={item.text === "Notifications"}
              userId={userId}
            />
          ))}
        </ul>
      </div>

      {/* Mobile Floating Menu Tab - Only visible on mobile */}
      <div className="mobile-menu-container">
        <FloatingMenuTab
          isOpen={isMobileMenuOpen}
          onToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          initialPosition={{ y: window.innerHeight / 2 - 40 }}
        >
          <nav className="mobile-nav-content">
            {/* Dashboard */}
            <button
              className={`menu-item ${isActive(`/talent-dashboard/${userId}`) ? 'active' : ''}`}
              onClick={() => handleNavigation('Dashboard', `/talent-dashboard/${userId}`)}
            >
              <img src={IconDash} alt="" className="menu-icon" />
              <span>Dashboard</span>
            </button>

            {/* Public Profile */}
            <button
              className={`menu-item ${isActive(getEpkPath) ? 'active' : ''}`}
              onClick={() => handleNavigation('Public Profile', getEpkPath)}
            >
              <img src={IconPublProfile} alt="" className="menu-icon" />
              <span>Public Profile</span>
            </button>

            {/* Notifications */}
            <button
              className={`menu-item ${isActive(`/talent-notifications/${userId}`) ? 'active' : ''}`}
              onClick={() => handleNavigation('Notifications', `/talent-notifications/${userId}`)}
            >
              <AnimatedBellIcon
                userId={userId}
                userType="talent"
                iconSrc={IconNotifications}
                className="menu-icon"
              />
              <span>Notifications</span>
            </button>

            {/* Inbox */}
            <button
              className={`menu-item ${isActive(`/talent-inbox/${userId}`) ? 'active' : ''}`}
              onClick={() => handleNavigation('Inbox', `/talent-inbox/${userId}`)}
            >
              <img src={IconMessages} alt="" className="menu-icon" />
              <span>Inbox</span>
            </button>

            {/* Gig Calendar */}
            <button
              className={`menu-item ${isActive(`/talent-calendar/${userId}`) ? 'active' : ''}`}
              onClick={() => handleNavigation('Gig Calendar', `/talent-calendar/${userId}`)}
            >
              <img src={IconCalendar} alt="" className="menu-icon" />
              <span>Gig Calendar</span>
            </button>

            {/* Gig Applications */}
            <button
              className={`menu-item ${isActive(`/talent-applications/${userId}`) ? 'active' : ''}`}
              onClick={() => handleNavigation('Gig Applications', `/talent-applications/${userId}`)}
            >
              <img src={GigApplicIcon} alt="" className="menu-icon" />
              <span>Gig Applications</span>
            </button>

            {/* Bookings Management */}
            <button
              className={`menu-item ${isActive(`/booking-management/${userId}`) ? 'active' : ''}`}
              onClick={() => handleNavigation('Bookings Management', `/booking-management/${userId}`)}
            >
              <img src={IconUpcomEvent} alt="" className="menu-icon" />
              <span>Bookings Management</span>
            </button>

            {/* Rider Management */}
            <button
              className={`menu-item ${isActive(`/talent-riders/${userId}`) ? 'active' : ''}`}
              onClick={() => handleNavigation('Rider Management', `/talent-riders/${userId}`)}
            >
              <img src={RiderManageIcon} alt="" className="menu-icon" />
              <span>Rider Management</span>
            </button>

            {/* Contracts Locker */}
            <button
              className={`menu-item ${isActive(`/talent-contracts-locker/${userId}`) ? 'active' : ''}`}
              onClick={() => handleNavigation('Contracts Locker', `/talent-contracts-locker/${userId}`)}
            >
              <img src={IconContract} alt="" className="menu-icon" />
              <span>Contracts Locker</span>
            </button>

            {/* Analytics */}
            <button
              className={`menu-item ${isActive(`/talent-analytics/${userId}`) ? 'active' : ''}`}
              onClick={() => handleNavigation('Analytics', `/talent-analytics/${userId}`)}
            >
              <img src={IconAnalytics} alt="" className="menu-icon" />
              <span>Analytics</span>
            </button>

            {/* Ratings & Reviews */}
            <button
              className={`menu-item ${isActive(`/talent-rating/${userId}`) ? 'active' : ''}`}
              onClick={() => handleNavigation('Ratings & Reviews', `/talent-rating/${userId}`)}
            >
              <img src={IconRating} alt="" className="menu-icon" />
              <span>Ratings & Reviews</span>
            </button>

            {/* Settings */}
            <button
              className={`menu-item ${isActive(`/talent-settings/${userId}`) ? 'active' : ''}`}
              onClick={() => handleNavigation('Settings', `/talent-settings/${userId}`)}
            >
              <img src={IconSettings} alt="" className="menu-icon" />
              <span>Settings</span>
            </button>

            {/* News - Coming Soon */}
            <button
              className="menu-item coming-soon"
              onClick={() => handleNavigation('News', `/talent-news/${userId}`, true)}
              disabled
            >
              <img src={IconNews} alt="" className="menu-icon" style={{ filter: 'grayscale(100%)' }} />
              <span>News</span>
              <span className="badge">Coming Soon</span>
            </button>

            {/* Dashboard Tour */}
            <button
              className={`menu-item ${process.env.NODE_ENV === 'production' ? 'coming-soon' : ''}`}
              onClick={handleStartTour}
              disabled={process.env.NODE_ENV === 'production'}
            >
              <img src={TourIcon} alt="" className="menu-icon" style={{ filter: process.env.NODE_ENV === 'production' ? 'grayscale(100%)' : 'none' }} />
              <span>Dashboard Tour</span>
              {process.env.NODE_ENV === 'production' && <span className="badge">Coming Soon</span>}
            </button>

            {/* Profile Verification - Coming Soon */}
            <button
              className="menu-item coming-soon"
              onClick={() => handleNavigation('Profile Verification', `/talent-verification/${userId}`, true)}
              disabled
            >
              <img src={IconVerification} alt="" className="menu-icon" style={{ filter: 'grayscale(100%)' }} />
              <span>Profile Verification</span>
              <span className="badge">Coming Soon</span>
            </button>
          </nav>
        </FloatingMenuTab>
      </div>

      <style>{`
        /* Desktop styles */
        @media (min-width: 1201px) {
          .mobile-menu-container {
            display: none !important;
          }
        }

        /* Mobile and tablet styles */
        @media (max-width: 1200px) {
          .desktop-sidebar {
            display: none !important;
          }
        }

        /* Mobile Navigation Content Styles */
        .mobile-nav-content {
          display: flex;
          flex-direction: column;
          gap: 3px;  /* Keeping at 3px */
        }

        .mobile-nav-content .menu-item {
          display: flex;
          align-items: center;
          gap: 11px;  /* Increased by 10% */
          padding: 11px 15px;  /* Increased by 10% */
          background: none;
          border: none;
          color: white;
          font-family: 'Montserrat', sans-serif;
          font-size: 14px;  /* Increased by 10% (back to original) */
          font-weight: 600;
          text-align: left;
          cursor: pointer;
          transition: all 0.2s ease;
          border-radius: 8px;  /* Increased by 10% (back to original) */
          width: 100%;
        }

        .mobile-nav-content .menu-item:hover:not(.coming-soon) {
          background-color: rgba(255, 255, 255, 0.1);
          transform: translateX(4px);
        }

        .mobile-nav-content .menu-item.active {
          color: #f85008;
          background-color: rgba(248, 80, 8, 0.1);
        }

        .mobile-nav-content .menu-item.coming-soon {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .mobile-nav-content .menu-icon {
          width: 20px;  /* Increased by 10% (back to original) */
          height: 20px;  /* Increased by 10% (back to original) */
          object-fit: contain;
          flex-shrink: 0;
        }

        .mobile-nav-content .menu-item.active .menu-icon {
          filter: brightness(0) saturate(100%) invert(39%) sepia(84%) saturate(3681%) hue-rotate(6deg) brightness(100%) contrast(96%);
        }

        .mobile-nav-content .badge {
          margin-left: auto;
          font-size: 10px;  /* Keeping at 10px */
          background-color: #f85008;
          color: white;
          padding: 2px 6px;  /* Increased by 10% (back to original) */
          border-radius: 10px;  /* Increased by 10% (back to original) */
        }
      `}</style>
    </>
  );
};

export default TalentSidebar;